import { useState, useCallback } from "react";
import { memoryStore } from "../utils/storage";

const STORAGE_KEY = "pension_hachshara_v1";

function load() {
  try {
    const raw = memoryStore.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function save(imports) {
  try { memoryStore.setItem(STORAGE_KEY, JSON.stringify(imports)); } catch {}
}

export function useHachsharaStore() {
  const [imports, setImports] = useState(() => load());

  const addImport = useCallback(({ companyCode, fileName, reportDate, sourceType, members }) => {
    const entry = {
      id: String(Date.now()),
      companyCode,
      fileName,
      reportDate,
      sourceType,
      importedAt: new Date().toISOString(),
      members, // [{ rawId, taz, clientId: null, funds }]
    };
    setImports(prev => {
      // Dedup: same file name always replaces; same company+month replaces only when a
      // month is set (never treat null==null as a match — that silently dropped older imports).
      const filtered = prev.filter(imp =>
        imp.fileName !== fileName &&
        !(reportDate && imp.companyCode === companyCode && imp.reportDate === reportDate)
      );
      const next = [...filtered, entry];
      save(next);
      return next;
    });
    return entry.id;
  }, []);

  // Link a member's ת"ז to an existing client UUID
  const linkMember = useCallback((importId, taz, clientId) => {
    setImports(prev => {
      const next = prev.map(imp =>
        imp.id !== importId ? imp : {
          ...imp,
          members: imp.members.map(m =>
            m.taz === taz ? { ...m, clientId } : m
          ),
        }
      );
      save(next);
      return next;
    });
  }, []);

  const removeImport = useCallback((importId) => {
    setImports(prev => {
      const next = prev.filter(i => i.id !== importId);
      save(next);
      return next;
    });
  }, []);

  const removeImportByFileName = useCallback((fileName) => {
    const current = load();
    const next = current.filter(i => i.fileName !== fileName);
    if (next.length !== current.length) save(next);
    setImports(next);
  }, []);

  // Return all import records for a given client UUID
  const getClientImports = useCallback((clientId) => {
    return imports.filter(imp =>
      imp.members.some(m => m.clientId === clientId)
    );
  }, [imports]);

  return { imports, addImport, linkMember, removeImport, removeImportByFileName, getClientImports };
}
